import { useIsomorphicLayoutEffect } from '@heidi/utils';
import { useEffect, useMemo, useRef } from 'react';

import { type FooterRailId } from './footer-rail.constants';
import { splitFooterRail } from './split-footer-rail';
import { useFooterRailMeasure } from './use-footer-rail-measure';

export interface FooterOverflow {
  railIds: readonly FooterRailId[];
  /** Empty when everything fits. */
  moreIds: readonly FooterRailId[];
  /** Attach to the footer root the measure pass keys off. */
  footerRef: (element: HTMLElement | null) => void;
}

export interface UseFooterOverflowArgs {
  enabled: boolean;
  /** Visible collapsible ids in priority order. */
  orderedIds: readonly FooterRailId[];
  /** Never collapse this pass (e.g. Remote while a device is linked). */
  pinnedIds: readonly FooterRailId[];
}

const NO_MORE: readonly FooterRailId[] = [];

/**
 * Composes measurement + the pure split. Measuring in a layout effect flushes the
 * split before paint, so the first render-all-then-collapse pass is invisible. A
 * runtime fail-safe returns everything uncollapsed whenever measurement can't be
 * trusted (pre-measure, SSR/jsdom, non-positive or non-finite geometry).
 */
export function useFooterOverflow({
  enabled,
  orderedIds,
  pinnedIds,
}: UseFooterOverflowArgs): FooterOverflow {
  const { footerRef, measurement, measure } = useFooterRailMeasure(enabled);
  const wasOverflowingRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure, orderedIds, pinnedIds]);

  const result = useMemo(() => {
    const untrustworthy =
      !enabled ||
      !measurement.hasMeasured ||
      measurement.availableHeight <= 0 ||
      !Number.isFinite(measurement.reservedHeight) ||
      measurement.reservedHeight < 0;

    if (untrustworthy) {
      return { railIds: orderedIds, moreIds: NO_MORE };
    }

    const split = splitFooterRail({
      orderedIds,
      pinnedIds,
      heights: measurement.heights,
      moreHeight: measurement.moreHeight,
      availableHeight: measurement.availableHeight,
      reservedHeight: measurement.reservedHeight,
      wasOverflowing: wasOverflowingRef.current,
    });

    // Never collapse an id we've never measured — the estimate could be wrong the
    // frame it first appears; render it and re-measure.
    const collapsingUnmeasured = split.moreIds.some((id) => !measurement.heights.has(id));
    if (collapsingUnmeasured) {
      return { railIds: orderedIds, moreIds: NO_MORE };
    }

    return split;
  }, [enabled, orderedIds, pinnedIds, measurement]);

  // Committed passes only: writing during render would let a discarded
  // StrictMode/concurrent pass leave this stale for the next split.
  useEffect(() => {
    wasOverflowingRef.current = result.moreIds.length > 0;
  }, [result]);

  return { railIds: result.railIds, moreIds: result.moreIds, footerRef };
}
